import React, { useState, useMemo } from 'react';
import CPPUtils from 'services/CPPUtils';
import { DefaultCheckbox } from 'services/UIUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import TextareaAutosize from 'components/Common/TextAreaAutosize';
import Icon from 'components/Utils/NewIcon';
import PharmacyBadge from 'components/Pharmacy/PharmacyBadge';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { setFormValues } from 'hooks/useReduxForm';

const ACTIVE_TAB = 'prescription';

interface RefillApprovalProps {
  refill: any;
  patient: any;
  setActiveCPPTab: (tab: string) => void;
  onClose: () => void;
}

const RefillApproval = ({ refill, patient, setActiveCPPTab, onClose }: RefillApprovalProps) => {
  const dispatch = useAppDispatch();

  const formName = CPPUtils.getFormName(ACTIVE_TAB, patient.id);
  const formStack = useAppSelector((state) => (state as any).rhfForms[formName]?.stack);

  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [physicianNote, setPhysicianNote] = useState(refill.physician_note || '');
  const [loading, setLoading] = useState(false);

  const isPending = !refill.status || refill.status === 'pending';

  const selectedMedications = useMemo(() => {
    return refill.medications.filter((medication: any) => checked[medication.name]);
  }, [refill, checked]);

  async function onSubmit() {
    let refillValue = {
      ...refill,
      medications: refill.medications.map((medication: any) => {
        return {
          ...medication,
          enabled: !!checked[medication.name],
        };
      }),
      status: 'completed',
      physician_note: physicianNote,
    };

    if (selectedMedications.length === 0) {
      setLoading(true);
      let { id, patient_id } = refillValue;
      let result = await dispatch(
        api.HealthRecords.editPrescriptionRefill({
          id,
          patientId: patient_id,
          prescriptionRefill: refillValue,
        }),
        { errorTitle: 'Prescription Refill' },
      );
      setLoading(false);
      if (result) onClose();
    } else {
      let stack = formStack;
      dispatch(
        setFormValues({
          formName,
          values: {
            medications: selectedMedications,
            originalPharmacy: refill.pharmacy,
            refill: refillValue,
            stack,
          },
        }),
      );
      setActiveCPPTab(ACTIVE_TAB);
      onClose();
    }
  }

  return (
    <div className="refill-approval">
      {!!refill.pharmacy && <PharmacyBadge pharmacy={refill.pharmacy} />}
      {!!refill.note && (
        <>
          <span className="small-title">Patient Notes</span>
          <pre>{refill.note}</pre>
          <br />
        </>
      )}
      <div className="medications">
        <span className="small-title">Requested Medications</span>
        {refill.medications.map((medication: any) => {
          let key = medication.name;
          return (
            <div className="medication" key={key}>
              {!isPending && (
                <>
                  <Icon
                    style={{ display: 'inline-block', marginRight: '10px' }}
                    width="15px"
                    fontAwesomeStyle="fad"
                    iconStyle={{
                      '--fa-primary-color': 'white',
                      '--fa-secondary-opacity': medication.enabled ? 1.0 : 0.25,
                    }}
                    className="blue"
                    src="check-circle"
                    alt={medication.enabled ? 'Approved' : 'Not approved'}
                  />
                  <span>{medication.name}</span>
                </>
              )}
              {isPending && (
                <DefaultCheckbox
                  value={!!checked[key]}
                  onChange={() => setChecked({ ...checked, [key]: !checked[key] })}
                  text={medication.name}
                />
              )}
            </div>
          );
        })}
      </div>
      <br />
      <div className="medications">
        <span className="small-title">Practitioner Note</span>
        {!isPending && <pre>{physicianNote}</pre>}
        {isPending && (
          <TextareaAutosize
            name="physician_note"
            value={physicianNote}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              setPhysicianNote(event.target.value)
            }
          />
        )}
      </div>
      {isPending && (
        <center>
          <DefaultButton
            name={
              selectedMedications.length === 0
                ? 'Reject Request'
                : `Select ${selectedMedications.length} Medications`
            }
            style={{ width: '80%', marginTop: '20px' }}
            className={selectedMedications.length === 0 ? 'red' : 'blue'}
            loading={loading}
            onClick={onSubmit}
          />
        </center>
      )}
    </div>
  );
};

export default React.memo(RefillApproval);
